import {
    Component,
    computed,
    DestroyRef,
    effect,
    HostListener,
    OnDestroy,
    OnInit,
    signal,
    viewChild,
    ViewContainerRef,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { DEFAULT_INTERRUPTSOURCES, Idle } from '@ng-idle/core';
import { TranslateService } from '@ngx-translate/core';
import { TokenInterceptor } from 'authentication/interceptors/token.interceptor';
import { AuthenticationService } from 'authentication/services/authentication.service';
import { debounce, throttle } from 'lodash-es';
import { filter, map, Subscription } from 'rxjs';
import { IS_SHIJI_USER_KEY, TOKEN_KEY } from 'shared/constants/general-constants';
import { IMAGES } from 'shared/constants/imagePaths.constants';
import { SELECTED_LANGUAGE_STORAGE_KEY, supportedLanguages } from 'shared/constants/languages';
import { WebsocketConnectionKeys } from 'shared/enums/websocket-connection-keys.enum';
import { minutesToSeconds } from 'shared/functions/minutes-to-seconds.function';
import { ContextAwareSidebarType } from 'shared/interfaces/context-aware-sidebar.interface';
import { FeatureFlagEnum } from 'shared/interfaces/environment.interface';
import { ModalService } from 'shared/services';
import { EventBusService } from 'shared/services/event-bus.service';
import { FeatureFlagService } from 'shared/services/feature-flag.service';
import { GlobalConfigService } from 'shared/services/global-config.service';
import { GlobalEventListenerService } from 'shared/services/global-event-listener.service';
import { LayoutService } from 'shared/services/layout.service';
import { SidebarService } from 'shared/services/sidebar.service';
import { SignalRService } from 'shared/services/signalR.service';
import { StorageService } from 'shared/services/storage.service';
import { TooltipService } from 'shared/services/tooltip.service';
import { UserApiService } from 'shared/services/userApi.service';
import { unsubscribeFromAll } from 'shared/util/unsubscribeFromAll';

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    providers: [GlobalEventListenerService],
    standalone: false,
})
export class AppComponent implements OnInit, OnDestroy {
    readonly loaderExcludedUrls = [
        'supplierManagementApi/suppliers/get-suppliers-token-limit-information', // Supplier AI Features grid spinner request
        'mcpApi/ImageEditing/edit',
        'visualManagementApi/listing-assets/ai-edit-asset',
    ];

    protected IMAGES = IMAGES;
    private readonly SCROLL_DEBOUNCE_TIME = 50;
    private readonly SCROLL_THROTTLE_TIME = 50;

    private countdown: number | null = null;
    private sessionInactivityModalIndex: number | null = null;
    private subscriptions: { [key: string]: Subscription } = {};
    private idleSeconds!: number;
    private forceTimeoutSeconds!: number;

    private tooltipHost = viewChild('tooltipHost', { read: ViewContainerRef });
    protected readonly FeatureFlagEnum = FeatureFlagEnum;
    private modalHost = viewChild('modalHost', { read: ViewContainerRef });

    protected readonly isSidebarExpanded = this.sidebarService.isExpanded;

    // Will wait SCROLL_DEBOUNCE_TIME ms after the last scroll event before firing
    public scrollDebounced = debounce(
        (event: any) => {
            this.eventBus.dispatch('scrollDebounced', event);
        },
        this.SCROLL_DEBOUNCE_TIME,
        { leading: false, trailing: true },
    );

    // Will fire the scroll and then ignore subsequent scroll events for SCROLL_THROTTLE_TIME ms
    public scrollThrottled = throttle(
        (event: any) => {
            this.eventBus.dispatch('scrollThrottled', event);
        },
        this.SCROLL_THROTTLE_TIME,
        { leading: true, trailing: false },
    );

    @HostListener('window:beforeunload', ['$event'])
    beforeUnloadHandler(event: Event) {
        this.signalRService.stopConnections();
    }

    public manualLayoutOverrides = signal<{ header: boolean | null; sidebar: boolean | null }>({
        header: null,
        sidebar: null,
    });

    public layout = computed(() => {
        const loggedIn = this.authenticationService.isLoggedIn();
        const overrides = this.manualLayoutOverrides();

        return {
            header: overrides.header !== null ? overrides.header : loggedIn,
            sidebar: overrides.sidebar !== null ? overrides.sidebar : loggedIn,
        };
    });

    readonly isBulkEditPage = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event) => /\/listings\/bulk\//.test((event as NavigationEnd).url)),
        ),
        { initialValue: false },
    );

    readonly isListingDetailsPage = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event) => {
                const url = (event as NavigationEnd).url;
                return (
                    (url.includes('/listings/details/') || url.includes('/listings/bulk/')) &&
                    !/\/listings\/bulk\//.test(url) &&
                    !/\/outlet\/[^/]+/.test(url) &&
                    !/\/rooms\/(?!new(?:[/?#]|$))[^/]+/.test(url)
                );
            }),
        ),
        { initialValue: false },
    );

    readonly isOutletDetailsPage = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event) => /\/outlet\/[^/]+/.test((event as NavigationEnd).url)),
        ),
        { initialValue: false },
    );

    readonly isRoomsDetailsPage = toSignal(
        this.router.events.pipe(
            filter((event) => event instanceof NavigationEnd),
            map((event) => {
                const url = (event as NavigationEnd).url;
                return /\/rooms\/[^/]+/.test(url) && !/\/rooms\/new(?:[/?#]|$)/.test(url);
            }),
        ),
        { initialValue: false },
    );

    sidebarType = computed(() => {
        if (this.isBulkEditPage()) {
            return ContextAwareSidebarType.BULK;
        } else if (this.isListingDetailsPage()) {
            return ContextAwareSidebarType.LISTING;
        } else if (this.isOutletDetailsPage()) {
            return ContextAwareSidebarType.OUTLET;
        } else if (this.isRoomsDetailsPage()) {
            return ContextAwareSidebarType.ROOM;
        } else {
            return null;
        }
    });

    constructor(
        private eventBus: EventBusService,
        private translate: TranslateService,
        private storage: StorageService,
        private globalConfigService: GlobalConfigService,
        private userApiService: UserApiService,
        private layoutService: LayoutService,
        private destroyRef: DestroyRef,
        private authenticationService: AuthenticationService,
        private idle: Idle,
        private tokenInterceptor: TokenInterceptor,
        private modalService: ModalService,
        private signalRService: SignalRService,
        private router: Router,
        private tooltipService: TooltipService,
        private globalEventListenerService: GlobalEventListenerService,
        private sidebarService: SidebarService,
        public featureFlagService: FeatureFlagService,
    ) {
        const selectedLanguage = (this.storage.getFromLocal(SELECTED_LANGUAGE_STORAGE_KEY) as string) ?? supportedLanguages[0].languageCode;

        if (selectedLanguage) {
            translate.setFallbackLang(selectedLanguage);
            translate.use(selectedLanguage).pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
        } else {
            translate.setFallbackLang('en');
        }

        this.userApiService
            .getLoginInformation()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((loginInformation) => {
                if (loginInformation.isShiji) {
                    this.globalConfigService.getSupplierClasses().pipe(takeUntilDestroyed(this.destroyRef)).subscribe();
                }
            });

        effect(() => {
            const tooltipHost = this.tooltipHost();
            if (tooltipHost) {
                this.tooltipService.initHost(tooltipHost);
            }
        });

        effect(() => {
            const modalHost = this.modalHost();
            if (modalHost) {
                this.modalService.initHost(modalHost);
            }
        });
    }

    public ngOnInit() {
        this.userApiService
            .getLoginInformationContinuously()
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((loginInfo) => {
                unsubscribeFromAll(this.subscriptions);

                this.idleSeconds = minutesToSeconds(loginInfo.idleTime);
                this.forceTimeoutSeconds = minutesToSeconds(loginInfo.forceLogoutTime);
                this.stopIdleWatch();
                this.startIdleWatch();
                this.processInactivity();

                this.signalRService.startConnectionByKey(WebsocketConnectionKeys.USERS);

                if (this.featureFlagService.featureFlags()[FeatureFlagEnum.LISTING_CHANNEL_COMPARISON]) {
                    this.signalRService.startConnectionByKey(WebsocketConnectionKeys.LISTING_SNAPSHOTS);
                }
            });

        this.registerLogoutEventListener();
        this.globalEventListenerService.registerGlobalListeners();

        if (this.storage.getFromLocal(TOKEN_KEY) && typeof this.storage.getFromLocal(IS_SHIJI_USER_KEY) === 'boolean') {
            this.userApiService.getCurrentUser();
        }

        this.layoutService.registerLayoutComponent(this);
    }

    public handleScroll(event: any) {
        this.scrollDebounced(event);
        this.scrollThrottled(event);
    }

    public ngOnDestroy() {
        this.stopIdleWatch();
        unsubscribeFromAll(this.subscriptions);
    }

    private startIdleWatch() {
        this.idle.setIdle(this.idleSeconds); // how long can they be inactive before considered idle, in seconds
        // Timeout OK
        this.idle.setTimeout(this.forceTimeoutSeconds); // how long can they be idle before considered timed out, in seconds
        this.idle.setInterrupts(DEFAULT_INTERRUPTSOURCES);
        this.idle.watch();
    }

    private stopIdleWatch() {
        this.idle.stop();
        this.countdown = null;
        this.idle.clearInterrupts();
    }

    private processInactivity() {
        this.subscriptions.onIdleStart = this.idle.onIdleStart.subscribe(() => {
            if (this.authenticationService.isLoggedIn()) {
                //Show modal
                const modalReference = this.modalService.openSessionInactivityModal(this.countdown ?? 0, 'top');
                this.sessionInactivityModalIndex = modalReference.index;
                this.subscriptions.modal = modalReference.onClose.subscribe((result) => {
                    this.sessionInactivityModalIndex = -1;

                    if (result) {
                        this.authenticationService.logout(this.router?.url || '/');
                    } else {
                        //Refresh token
                        this.tokenInterceptor
                            .getNewToken()
                            .pipe(takeUntilDestroyed(this.destroyRef))
                            .subscribe((newToken) => {
                                this.storage.setInLocal(TOKEN_KEY, newToken);
                            });
                        this.stopIdleWatch();
                        this.startIdleWatch();
                    }
                });
            }
        });

        // do something when the user has timed out
        this.subscriptions.onIdleTimeout = this.idle.onTimeout.subscribe(() => {
            if (this.sessionInactivityModalIndex !== null && this.sessionInactivityModalIndex !== -1) {
                this.modalService.closeModal(this.sessionInactivityModalIndex, true);
                this.authenticationService.isForceLogout = true;
                this.authenticationService.logout(this.router?.url || '/');
            }
        });
    }

    private registerLogoutEventListener() {
        this.eventBus
            .getObservableForEventStream('logout')
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe(() => {
                this.stopIdleWatch();
                unsubscribeFromAll(this.subscriptions);
                this.signalRService.stopConnections();
            });
    }
}
